import type { ReactNode } from "react";
import { Avatar } from "../components/avatar";
import { Button } from "../components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/dropdown-menu";
import { Icon } from "../components/icon";
import type { IconName } from "../icons";
import { cn } from "../lib/cn";
import { AppLink } from "./link";

export type NotificationTone = "danger" | "success" | "warning" | "info";

const NOTIFICATION_TONE: Record<NotificationTone, string> = {
  danger: "text-danger",
  success: "text-success",
  warning: "text-warning",
  info: "text-primary-ink",
};

export interface NotificationItem {
  id: string;
  icon: IconName;
  tone: NotificationTone;
  title: string;
  time: string;
  href: string;
}

export interface NotificationsMenuProps {
  items: readonly NotificationItem[];
  unread: boolean;
  onMarkAllRead: () => void;
  labels: { trigger: string; heading: string; markAllRead: string; unread: string };
}

export function NotificationsMenu({ items, unread, onMarkAllRead, labels }: NotificationsMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" iconOnly aria-label={unread ? `${labels.trigger} (${labels.unread})` : labels.trigger} className="relative">
          <Icon name="bell" />
          {unread ? <span aria-hidden="true" className="absolute right-2 top-2 size-2 rounded-pill bg-signal-500 ring-2 ring-surface" /> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="text-sm font-bold">{labels.heading}</span>
          <button type="button" className="text-xs font-semibold text-primary-ink hover:underline" onClick={onMarkAllRead}>
            {labels.markAllRead}
          </button>
        </div>
        {items.map((item) => (
          <DropdownMenuItem key={item.id} asChild className="items-start gap-3 rounded-none border-b border-line px-4 py-3 last:border-0">
            <AppLink href={item.href}>
              <Icon name={item.icon} className={cn("mt-0.5", NOTIFICATION_TONE[item.tone])} />
              <span>
                <span className="block text-sm text-fg">{item.title}</span>
                <span className="mt-0.5 block text-xs text-fg-subtle">{item.time}</span>
              </span>
            </AppLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface UserMenuProps {
  name: string;
  role: string;
  triggerLabel: string;
  children: ReactNode;
}

/** Sidebar footer user button (brand avatar) opening upward. */
export function UserMenu({ name, role, triggerLabel, children }: UserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={triggerLabel}
        className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-chrome-3 data-[state=open]:bg-chrome-3"
      >
        <Avatar name={name} className="bg-brand-500 text-ink-950 dark:bg-brand-500 dark:text-ink-950" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-bold text-white">{name}</span>
          <span className="block text-[11px] text-chrome-muted">{role}</span>
        </span>
        <Icon name="more-vertical" className="text-chrome-muted" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface MenuLinkProps {
  href: string;
  icon: IconName;
  children: ReactNode;
  tone?: "default" | "danger";
}

export function MenuLink({ href, icon, children, tone = "default" }: MenuLinkProps) {
  return (
    <DropdownMenuItem asChild tone={tone}>
      <AppLink href={href}>
        <Icon name={icon} />
        {children}
      </AppLink>
    </DropdownMenuItem>
  );
}
